import { ReactNode } from "react";
import type { RJSFSchema } from "@rjsf/utils";
import { isEmpty } from "lodash";
import { arrayHasAtLeastTwoItems } from "common/utils/arrays";
import { getFieldTypeFromSchema } from "common/utils/formStructure";
import { Button } from "../../../../Common/Buttons/Button";
import { Icon } from "../../../../Common/Icons/LucideIcons";
import { MenuButtonsWrapper } from "./__styles__/SubmissionsBuilder";
import {
  type SubmissionsBuilderFormData,
  useBuilderFormContext,
  useBuilderWatch,
} from "./form/form";
import { useSubmitForm } from "./form/submission/submit";
import {
  SUBMISSIONS_BUILDER_MODE,
  useSubmissionsBuilderContext,
  useSubmissionsBuilderMode,
} from "./SubmissionsBuilderContext";

function hasNoFieldsOrEmptyGroups(
  formStructure: SubmissionsBuilderFormData["formStructure"],
): boolean {
  const properties = formStructure?.schema?.properties;
  const uiSchema = formStructure?.uiSchema ?? {};

  if (isEmpty(properties)) {
    return true;
  }

  for (const [key, schema] of Object.entries(properties as RJSFSchema)) {
    const fieldUiSchema = uiSchema[key] ?? {};
    const fieldType = getFieldTypeFromSchema(schema, fieldUiSchema);

    if (fieldType === "group") {
      // Allow groups that have description content (informational sections)
      // but no child fields - these are intentionally text-only
      const hasDescription = Boolean(schema.description);
      if (isEmpty(schema.properties) && !hasDescription) {
        return true;
      }
    }
  }

  return false;
}

export function MenuButtons({
  chatToggleButton,
}: {
  chatToggleButton?: ReactNode;
}) {
  const { formState } = useBuilderFormContext();
  const [mode, setMode] = useSubmissionsBuilderMode();
  const { existingSubmissionType } = useSubmissionsBuilderContext();
  const hasVersionHistory = arrayHasAtLeastTwoItems(
    existingSubmissionType?.versions ?? [],
  );
  const submitForm = useSubmitForm();

  const formStructure = useBuilderWatch("formStructure");
  const shouldDisableForEmptyFields = hasNoFieldsOrEmptyGroups(formStructure);

  const isSubmitDisabled =
    mode !== SUBMISSIONS_BUILDER_MODE.BUILD ||
    shouldDisableForEmptyFields ||
    (existingSubmissionType != null && !formState.isDirty);

  const canOpenVersionHistory =
    hasVersionHistory &&
    mode === SUBMISSIONS_BUILDER_MODE.BUILD &&
    !formState.isDirty;

  return (
    <MenuButtonsWrapper>
      {mode === SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY ?
        <Button
          variant="outline"
          size="small"
          onPress={() => setMode(SUBMISSIONS_BUILDER_MODE.BUILD)}
          title="Return to current version"
        />
      : canOpenVersionHistory ?
        <Icon
          iconName="history"
          color="contentSecondary"
          size={16}
          cursor="pointer"
          testId="version-history"
          onClick={() => setMode(SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY)}
        />
      : null}
      <Button
        variant="primary"
        size="small"
        isDisabled={isSubmitDisabled}
        isPending={formState.isSubmitting}
        onPress={() => submitForm()}
        title="Save"
      />
      {chatToggleButton}
    </MenuButtonsWrapper>
  );
}
